import { ActionButton } from '@app/src/components/ActionButtons'
import { sendNewIntercomMessage } from '@app/src/hooks/intercom'
import useOrganizationSettings from '@app/src/hooks/useOrganizationSettings'
import { CheckCircleOutline } from '@mui/icons-material'
import { Box, Stack, Typography } from '@mui/material'
import React from 'react'
import { useIntl } from 'react-intl'
import ConfigurationCollection from '../../ConfigurationCollection'
import AiScreeningLimitAlert from './AiScreeningLimitAlert'
import AiScreeningStats from './AiScreeningStats'

const AiScreeningInfoPage: React.FC = () => {
  const { formatMessage } = useIntl()
  const { settings: organizationSettings } = useOrganizationSettings()

  const remainingProviderScreenings = organizationSettings?.remainingProviderScreenings ?? 0

  const handleAddScreeningsClick = () => {
    const message = `${formatMessage({ id: 'aiScreening.intercomMessage.firstLineText' })}\n\n${formatMessage({
      id: 'aiScreening.intercomMessage.secondLineText',
    })}`
    sendNewIntercomMessage(message)
  }

  const actionButtons: ActionButton[] = [
    {
      label: formatMessage({ id: 'aiScreening.actionButtons.add' }),
      variant: 'contained',
      size: 'large',
      onClick: handleAddScreeningsClick,
    },
  ]

  return (
    <ConfigurationCollection actionButtons={actionButtons}>
      {remainingProviderScreenings <= 0 && <AiScreeningLimitAlert />}
      <Box bgcolor="common.white" mr={4} borderRadius={1}>
        <AiScreeningStats
          allowedProviderScreenings={organizationSettings?.allowedProviderScreenings ?? 0}
          totalProviderScreened={organizationSettings?.totalProviderScreened ?? 0}
          remainingProviderScreenings={remainingProviderScreenings}
        />
        <Stack bgcolor="grey.50" justifyContent="space-between">
          <Stack mt={2} direction="row" padding={2}>
            <Stack>
              <Typography fontWeight={500}> {formatMessage({ id: 'aiScreening.infoPage.heading' })}</Typography>
            </Stack>
          </Stack>
          <Stack padding={2} direction="row" justifyContent="space-between" spacing={2}>
            <CheckCircleOutline />
            <Stack direction="row" justifyContent="space-between" flexGrow={1}>
              <Stack>
                <Typography gutterBottom>{formatMessage({ id: 'aiScreening.infoPage.dataInMinutes' })}</Typography>
                <Typography gutterBottom variant="caption" color="textSecondary">
                  {formatMessage({ id: 'aiScreening.infoPage.dataInMinutesDescription' })}
                </Typography>
              </Stack>
            </Stack>
          </Stack>
          <Stack padding={2} direction="row" justifyContent="space-between" spacing={2}>
            <CheckCircleOutline />
            <Stack direction="row" justifyContent="space-between" flexGrow={1}>
              <Stack>
                <Typography gutterBottom>{formatMessage({ id: 'aiScreening.infoPage.dataWithConfidence' })}</Typography>
                <Typography gutterBottom variant="caption" color="textSecondary">
                  {formatMessage({ id: 'aiScreening.infoPage.dataWithConfidenceDescription' })}
                </Typography>
              </Stack>
            </Stack>
          </Stack>
          <Stack padding={2} direction="row" justifyContent="space-between" spacing={2}>
            <CheckCircleOutline />
            <Stack direction="row" justifyContent="space-between" flexGrow={1}>
              <Stack>
                <Typography gutterBottom>{formatMessage({ id: 'aiScreening.infoPage.manualAiScreening' })}</Typography>
                <Typography gutterBottom variant="caption" color="textSecondary">
                  {formatMessage({ id: 'aiScreening.infoPage.manualAiScreeningDescription' })}
                </Typography>
              </Stack>
            </Stack>
          </Stack>
          <Stack padding={2} direction="row" justifyContent="space-between" spacing={2}>
            <CheckCircleOutline />
            <Stack direction="row" justifyContent="space-between" flexGrow={1}>
              <Stack>
                <Typography gutterBottom>
                  {formatMessage({ id: 'aiScreening.infoPage.automateAiScreening' })}
                </Typography>
                <Typography gutterBottom variant="caption" color="textSecondary">
                  {formatMessage({ id: 'aiScreening.infoPage.automateAiScreeningDescription' })}
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </Box>
    </ConfigurationCollection>
  )
}

export default AiScreeningInfoPage
